import React, { useState, useEffect, useRef } from "react";
import * as S from "./styles";
import * as API from "../../api";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ModuleTareaDatosGenerales } from "../../components/ModuleTareaDatosGenerales";
import { Button } from "../../components/Button";
import { Loading } from "../../components/Loading";
import {
  METADATA_TAREA_POSTFIRMA_DATOS_GENERALES,
  METADATA_AUDITORIA_CAMPOS,
} from "../../constants";
import { ModuleAuditoriaCampos } from "../../components/ModuleAuditoriaCampos";
import {
  ModuleDatosPostFirma,
  TIPO_HITO,
  HITOS_OBLIGATORIOS,
  NOMBRE_HITO,
} from "../../components/ModuleDatosPostFirma";
import { usePermissions } from "../../context/PermissionsContext";

export const TareaPostFirma = () => {
  const { permissions } = usePermissions();
  const { idTarea } = useParams();
  const navigate = useNavigate();
  const [datosAuditoria, setDatosAuditoria] = useState([]);
  const [datosGenerales, setDatosGenerales] = useState({});
  const [hitos, setHitos] = useState([]);
  const [esEditable, setEsEditable] = useState(true);
  const [triggerRefresh, setTriggerRefresh] = useState(false);
  const [loading, setLoading] = useState(true);
  // DNE2E-131 (T3.1/T3.2): documentos de la reserva y listas de tipos por hito
  const [docsReserva, setDocsReserva] = useState([]);
  const [lists, setLists] = useState({});
  const [confirmCompletar, setConfirmCompletar] = useState(false);
  const guardarTodosRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    API.abrirTarea({ tarea: idTarea })
      .then((res) => {
        if (isMounted) {
          if (res.datosGenerales) setDatosGenerales(res.datosGenerales);
          if (res.notificacion) setDatosAuditoria(res.notificacion);
          if (res.hitos) setHitos(res.hitos);
          if (res.esEditable != null) setEsEditable(res.esEditable);
          if (res.documentosReserva) setDocsReserva(res.documentosReserva);
        }
      })
      .catch((error) => {
        API.DEVELOP && console.log(error);
        // Fallback: si abrirTarea falla, obtener datos desde el endpoint especifico
        if (isMounted) {
          API.obtenerTareaPostFirma({ idTarea })
            .then((res) => {
              if (isMounted) {
                if (res?.datosGenerales) setDatosGenerales(res.datosGenerales);
                if (res?.notificacion) setDatosAuditoria(res.notificacion);
                if (res?.hitos) setHitos(res.hitos);
                if (res?.esEditable != null) setEsEditable(res.esEditable);
                if (res?.documentosReserva) setDocsReserva(res.documentosReserva);
              }
            })
            .catch((err) => API.DEVELOP && console.log(err));
        }
      })
      .finally(() => setLoading(false));

    // DNE2E-131 (T3.1): listas de tipos de documento del modulo Post-Firma
    API.obtenerListas("TareaPostFirma", 0)
      .then((res) => isMounted && setLists(res))
      .catch((error) => API.DEVELOP && console.log(error));

    return () => (isMounted = false);
  }, [idTarea]);

  useEffect(() => {
    if (triggerRefresh) {
      API.obtenerTareaPostFirma({ idTarea }).then((res) => {
        if (res?.notificacion) setDatosAuditoria(res.notificacion);
        if (res?.hitos) setHitos(res.hitos);
        // DNE2E-131 (T1.4): recargar tambien datosGenerales para reevaluar
        // confirmacionFirmaCompletada tras completar un hito (habilitacion Escritura).
        if (res?.datosGenerales) setDatosGenerales(res.datosGenerales);
        // DNE2E-131 (T3.1): refrescar documentos de la reserva
        if (res?.documentosReserva) setDocsReserva(res.documentosReserva);
      });
      setTriggerRefresh(false);
    }
  }, [idTarea, triggerRefresh]);

  const solicitarCompletarTarea = () => {
    // Validar que los hitos obligatorios estén completados.
    // Reserva y Contrato Privado solo se validan si existen (solo uno viene informado).
    const hitosObligatoriosNoCompletados = HITOS_OBLIGATORIOS.filter((idTipo) => {
      const hito = hitos.find((h) => h.idTipo === idTipo);
      if (
        (idTipo === TIPO_HITO.RESERVA ||
          idTipo === TIPO_HITO.CONTRATO_PRIVADO ||
          idTipo === TIPO_HITO.ESCRITURA) &&
        !hito
      ) {
        return false;
      }
      return !hito || !hito.completado;
    });

    if (hitosObligatoriosNoCompletados.length > 0) {
      const nombres = hitosObligatoriosNoCompletados
        .map((idTipo) => NOMBRE_HITO[idTipo])
        .join(", ");
      toast.error(
        `No se puede completar la tarea. Los siguientes hitos obligatorios no están completados: ${nombres}.`
      );
      return;
    }

    setConfirmCompletar(true);
  };

  const completarTareaPostFirma = () => {
    setConfirmCompletar(false);
    setLoading(true);
    API.guardarCompletarTareaPostFirma({
      idTarea,
      idReserva: datosGenerales.idReserva,
      completarTarea: true,
    })
      .then((res) => {
        if (res.resultadoPeticion === 1) {
          toast.success("Tarea Completada");
          navigate("/");
        } else toast.error(res.mensajeUsuario);
      })
      .catch((error) => API.DEVELOP && console.log(error))
      .finally(() => setLoading(false));
  };

  const ocultarCampo = (idNotificacion, ocultar, eliminar) => {
    setLoading(true);
    const data = {};
    data.IdNotificacion = idNotificacion;
    data.Visibilidad = ocultar;
    data.Eliminado = eliminar;
    API.guardarVisibilidadNotificacionRevOp(data)
      .then((res) => {
        setTriggerRefresh(true);
        if (res.resultadoPeticion === 1) toast.success(res.mensajeUsuario);
        else toast.error(res.mensajeUsuario);
      })
      .catch((error) => API.DEVELOP && console.log(error))
      .finally(() => setLoading(false));
  };

  return (
    <S.Wrapper>
      {loading && <Loading />}

      {datosGenerales.esVentaDigital && (
        <h1 className="highlighted">Venta Digital</h1>
      )}
      {datosGenerales.crmForzadas && (
        <h1 className="highlighted2">Forzada CRM</h1>
      )}

      <ModuleAuditoriaCampos
        data={datosAuditoria}
        metadata={METADATA_AUDITORIA_CAMPOS}
        ocultar={ocultarCampo}
        access={permissions.TareaAnalisis}
      />

      <ModuleTareaDatosGenerales
        data={datosGenerales}
        metadata={METADATA_TAREA_POSTFIRMA_DATOS_GENERALES}
      />

      {datosGenerales.esFastrack && <h1 className="highlighted3">Fastrack</h1>}

      <ModuleDatosPostFirma
        hitos={hitos}
        datosGenerales={datosGenerales}
        access={permissions.TareaAnalisis}
        idTarea={idTarea}
        onHitoSaved={() => setTriggerRefresh(true)}
        guardarTodosRef={guardarTodosRef}
        readOnly={!esEditable}
        documentosReserva={docsReserva}
        lists={lists}
      />

      <S.Buttons>
        <a
          href={`/FichaReserva/${datosGenerales.idReserva}`}
          target="_blank"
          rel="noreferrer"
        >
          <Button label="Ver Ficha" disabled={!datosGenerales.idReserva} />
        </a>
        <div>
          <Button
            label="Guardar"
            onClick={() => guardarTodosRef.current && guardarTodosRef.current()}
            disabled={!esEditable || !permissions.TareaAnalisis?.Completar}
          />
          <Button
            label="Completar"
            onClick={() => solicitarCompletarTarea()}
            disabled={!esEditable || !permissions?.TareaAnalisis?.Completar}
          />
        </div>
      </S.Buttons>

      {confirmCompletar && (
        <S.ModalOverlay onClick={() => setConfirmCompletar(false)}>
          <S.ModalContent onClick={(e) => e.stopPropagation()}>
            <S.ModalHeader>Confirmar</S.ModalHeader>
            <S.ModalBody>
              <p>¿Está seguro de que desea completar la tarea? Esta acción no se puede deshacer.</p>
              <S.ModalButtons>
                <Button
                  label="Sí, completar"
                  onClick={() => completarTareaPostFirma()}
                />
                <Button
                  label="Cancelar"
                  onClick={() => setConfirmCompletar(false)}
                />
              </S.ModalButtons>
            </S.ModalBody>
          </S.ModalContent>
        </S.ModalOverlay>
      )}
    </S.Wrapper>
  );
};
